import React, { useState, Fragment } from 'react';
import T from 'prop-types';
import cx from 'classnames';
import sanitizeHtml from 'sanitize-html';
import he from 'he';
import { isEmpty } from 'lodash-es';
import { numberToSize, numberToCurrency } from '../utils';

const clearText = (text) =>
  he.decode(sanitizeHtml(text).replace(/[^\w\s\!@#$%^&*()+={}\[\].,;:]/gi, ''));

const OtherInformation = ({ metaData = {} }) => {
  const [deviceOpen, setDeviceOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const appProducts = metaData.app_products || [];

  const renderMainContent = (data, maxDevicesCount, maxLanguagesCount) => {
    const hasMoreDevices =
      maxDevicesCount &&
      metaData.device_compatibility &&
      metaData.device_compatibility.length > maxDevicesCount;
    const hasMoreLanguages =
      maxLanguagesCount &&
      metaData.languages_list &&
      metaData.languages_list.length > maxLanguagesCount;
    return (
      <Fragment>
        <div className="version-history-header">
          <h2 className="widget-title">Other Information</h2>
        </div>
        <div className="versions-history-truncate">
          <div
            className={cx('other-information-meta-data', { 'with-border': !isEmpty(appProducts) })}
          >
            {metaData.store_id && (
              <div className="other-information-meta-data-item">
                <span>Store ID</span>
                <a
                  className="link-default"
                  href={metaData.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {metaData.store_id}
                </a>
              </div>
            )}
            {metaData.bundle_id && (
              <div className="other-information-meta-data-item">
                <span>Bundle ID</span>
                <span>{metaData.bundle_id}</span>
              </div>
            )}
            {metaData.store !== 'google_play' && (
              <div className="other-information-meta-data-item">
                <span>File Size</span>
                <span>
                  {metaData.file_size > 0 ? numberToSize(metaData.file_size) : 'Varies with device'}
                </span>
              </div>
            )}
            {metaData.os_compatibility && (
              <div className="other-information-meta-data-item">
                <span>OS Compatibility</span>
                <span>{metaData.os_compatibility}</span>
              </div>
            )}
            {!isEmpty(metaData.device_compatibility) && (
              <div className="other-information-meta-data-item">
                <span>Device Compatibility</span>
                <span>
                  <div className={cx('show-more')}>
                    <div className={cx('show-more-box', { open: deviceOpen })}>
                      {metaData.device_compatibility.map(clearText).join(', ')}
                    </div>
                    {hasMoreDevices && (
                      <div className="d-flex justify-content-end">
                        <p
                          className="show-more-box-btn"
                          onClick={() => setDeviceOpen((prev) => !prev)}
                        >
                          {deviceOpen ? 'Hide' : 'Show'} Details
                        </p>
                      </div>
                    )}
                  </div>
                </span>
              </div>
            )}
            {metaData.age_rating && (
              <div className="other-information-meta-data-item">
                <span>Age Rating</span>
                <span>{metaData.age_rating}</span>
              </div>
            )}
            {metaData.has_widgets && (
              <div className="other-information-meta-data-item">
                <span>Widgets</span>
                <span>{metaData.has_widgets ? 'Yes' : 'No'}</span>
              </div>
            )}
            {metaData.has_app_clips && (
              <div className="other-information-meta-data-item">
                <span>App Clips</span>
                <span>{metaData.has_app_clips ? 'Yes' : 'No'}</span>
              </div>
            )}
            {metaData.has_apple_watch_app && (
              <div className="other-information-meta-data-item">
                <span>Apple Watch</span>
                <span>{metaData.has_apple_watch_app ? 'Yes' : 'No'}</span>
              </div>
            )}
            {metaData.privacy_url && (
              <div className="other-information-meta-data-item">
                <span>Privacy</span>
                <a
                  className="link-default"
                  href={metaData.privacy_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Policy
                </a>
              </div>
            )}
            {!isEmpty(metaData.languages_list) && (
              <div className="other-information-meta-data-item">
                <span>Languages</span>
                <span>
                  <div className={cx('show-more')}>
                    <div className={cx('show-more-box', { open: languageOpen })}>
                      {metaData.languages_list.join(', ')}
                    </div>
                    {hasMoreLanguages && (
                      <div className="d-flex justify-content-end">
                        <p
                          className="show-more-box-btn"
                          onClick={() => setLanguageOpen((prev) => !prev)}
                        >
                          {languageOpen ? 'Hide' : 'Show'} Details
                        </p>
                      </div>
                    )}
                  </div>
                </span>
              </div>
            )}
            {!isEmpty(metaData.interactive_elements) && (
              <div className="other-information-meta-data-item">
                <span>Interactive Elements</span>
                <span>
                  <div className="show-more">
                    <div className="show-more-box">{metaData.interactive_elements.join(', ')}</div>
                  </div>
                </span>
              </div>
            )}
            {!isEmpty(metaData.permissions) && (
              <div>
                <div className="other-information-meta-data-item">
                  <span>Permissions</span>
                  <span>
                    <div className={cx('show-more')}>
                      <div className={cx('show-more-box')}>
                        {metaData.permissions.map((item) => item.category).join(', ')}
                      </div>
                      <div className="d-flex justify-content-end">
                        <p
                          className="show-more-box-btn"
                          onClick={() => setPermissionsOpen((prev) => !prev)}
                        >
                          {permissionsOpen ? 'Hide' : 'Show'} Details
                        </p>
                      </div>
                    </div>
                  </span>
                </div>
                {permissionsOpen && (
                  <div>
                    {metaData.permissions.map((item) => (
                      <div key={item.category} className="other-information-meta-data-item">
                        <span>
                          {item.category}({item.permissions.length})
                        </span>
                        <span>
                          {item.permissions.map((permission) => (
                            <div key={permission.name} className="show-more mb-sm">
                              <div className="permission-title">{permission.name}</div>
                              <div className="show-more-box">{permission.description}</div>
                            </div>
                          ))}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {!isEmpty(appProducts) && (
            <div className="other-information-app-products">
              <h2 className="widget-title">In-App Products</h2>
              <div className="app-products">
                {data.map(({ name, price }, i) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <div key={i} className="app-products-item">
                    <span className="app-products-item-name">
                      {metaData.store === 'itunes_connect' && `${i + 1}.`} {name}
                    </span>
                    {price > 0 && (
                      <span className="app-products-item-price">
                        {numberToCurrency(price, { precision: 2 })}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Fragment>
    );
  };

  return (
    <div className="widget-container about-page-other-information">
      {renderMainContent(appProducts, 3, 3)}
    </div>
  );
};

OtherInformation.propTypes = {
  metaData: T.shape({
    store_id: T.oneOfType([T.string, T.number]),
    bundle_id: T.string,
    file_size: T.number,
    os_compatibility: T.string,
    device_compatibility: T.array,
    age_rating: T.string,
    has_widgets: T.bool,
    has_app_clips: T.bool,
    has_apple_watch_app: T.bool,
    privacy_url: T.string,
    languages_list: T.array,
    permissions: T.array,
    url: T.string,
    app_products: T.array,
    store: T.string.isRequired,
  }),
};

export default OtherInformation;
